class HeroCarouselComponent extends HTMLElement {
  connectedCallback() {
    this.slideshow = this.querySelector('slideshow-component.hero-carousel');
    this.sliderEl = this.querySelector('.hero-carousel__slider');
    this.barEl = this.querySelector('[data-hero-carousel-bar]');
    this.messageEl = this.querySelector('[data-hero-carousel-message]');
    this.ctaEl = this.querySelector('[data-hero-carousel-cta]');
    this.ctaWrapEl = this.querySelector('[data-hero-carousel-cta-wrap]');
    this.ctaLabelEl = this.querySelector('[data-hero-carousel-cta-label]');
    this.prevButton = this.querySelector('.hero-carousel__button--prev');
    this.nextButton = this.querySelector('.hero-carousel__button--next');

    if (!this.slideshow || !this.messageEl || !this.ctaEl || !this.ctaLabelEl) return;

    this.slides = Array.from(this.slideshow.querySelectorAll('[data-hero-slide="true"]'));
    if (!this.slides.length) return;

    this.updateFromSlide(this.slides[0]);

    this.onSlideChanged = this.onSlideChanged || this.handleSlideChanged.bind(this);
    this.slideshow.addEventListener('slideChanged', this.onSlideChanged);

    this.bindBarSwipe();
    this.bindAutoplay();
  }

  disconnectedCallback() {
    if (this.slideshow && this.onSlideChanged) {
      this.slideshow.removeEventListener('slideChanged', this.onSlideChanged);
    }

    if (this.barEl && this.onBarTouchStart && this.onBarTouchEnd) {
      this.barEl.removeEventListener('touchstart', this.onBarTouchStart);
      this.barEl.removeEventListener('touchend', this.onBarTouchEnd);
    }

    this.unbindAutoplay();
    this.stopAutoplay();
  }

  handleSlideChanged(event) {
    const currentSlide = event?.detail?.currentElement;
    if (!currentSlide) return;
    this.updateFromSlide(currentSlide);
  }

  updateFromSlide(slide) {
    const message = slide.dataset.barText || '';
    const ctaLabel = slide.dataset.ctaLabel || '';
    const ctaLink = slide.dataset.ctaLink || '';

    this.messageEl.textContent = message;

    if (ctaLabel && ctaLink) {
      this.ctaLabelEl.textContent = ctaLabel;
      this.ctaEl.setAttribute('href', ctaLink);
      this.ctaEl.removeAttribute('hidden');
      if (this.ctaWrapEl) this.ctaWrapEl.removeAttribute('hidden');
    } else {
      this.ctaLabelEl.textContent = '';
      this.ctaEl.setAttribute('hidden', 'hidden');
      this.ctaEl.removeAttribute('href');
      if (this.ctaWrapEl) this.ctaWrapEl.setAttribute('hidden', 'hidden');
    }
  }

  bindBarSwipe() {
    if (!this.barEl || this.slides.length < 2 || !this.prevButton || !this.nextButton) return;

    this.onBarTouchStart = (event) => {
      if (!event.touches || event.touches.length !== 1) return;
      if (event.target.closest('.hero-carousel__button, .slider-counter__link, .hero-carousel__cta')) return;

      const touch = event.touches[0];
      this.barTouchStartX = touch.clientX;
      this.barTouchStartY = touch.clientY;
    };

    this.onBarTouchEnd = (event) => {
      if (this.barTouchStartX == null || this.barTouchStartY == null) return;
      if (!event.changedTouches || event.changedTouches.length !== 1) return;

      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - this.barTouchStartX;
      const deltaY = touch.clientY - this.barTouchStartY;

      this.barTouchStartX = null;
      this.barTouchStartY = null;

      const swipeThreshold = 36;
      if (Math.abs(deltaX) < swipeThreshold || Math.abs(deltaX) <= Math.abs(deltaY)) return;

      if (deltaX < 0) {
        this.nextButton.click();
      } else {
        this.prevButton.click();
      }
    };

    this.barEl.addEventListener('touchstart', this.onBarTouchStart, { passive: true });
    this.barEl.addEventListener('touchend', this.onBarTouchEnd, { passive: true });
  }

  bindAutoplay() {
    if (!this.sliderEl || this.slides.length < 2 || !this.nextButton) return;

    const autoplaySetting = this.sliderEl.dataset.heroAutoplay || this.sliderEl.dataset.autoplay;
    this.autoplayEnabled = autoplaySetting === 'true';
    if (!this.autoplayEnabled) return;

    const configuredSpeed = Number(this.sliderEl.dataset.speed);
    this.autoplayDelay = Number.isFinite(configuredSpeed) && configuredSpeed > 0 ? configuredSpeed * 1000 : 5000;

    this.onAutoplayPause = () => this.stopAutoplay();
    this.onAutoplayResume = () => this.startAutoplay();
    this.onVisibilityChange = () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    };

    this.addEventListener('mouseenter', this.onAutoplayPause);
    this.addEventListener('mouseleave', this.onAutoplayResume);
    this.addEventListener('focusin', this.onAutoplayPause);
    this.addEventListener('focusout', this.onAutoplayResume);
    this.addEventListener('touchstart', this.onAutoplayPause, { passive: true });
    this.addEventListener('touchend', this.onAutoplayResume, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.startAutoplay();
  }

  unbindAutoplay() {
    if (!this.onAutoplayPause || !this.onAutoplayResume) return;

    this.removeEventListener('mouseenter', this.onAutoplayPause);
    this.removeEventListener('mouseleave', this.onAutoplayResume);
    this.removeEventListener('focusin', this.onAutoplayPause);
    this.removeEventListener('focusout', this.onAutoplayResume);
    this.removeEventListener('touchstart', this.onAutoplayPause);
    this.removeEventListener('touchend', this.onAutoplayResume);

    if (this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  startAutoplay() {
    if (!this.autoplayEnabled || !this.nextButton || !this.autoplayDelay) return;
    this.stopAutoplay();
    this.autoplayInterval = window.setInterval(() => {
      this.nextButton.click();
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    if (!this.autoplayInterval) return;
    window.clearInterval(this.autoplayInterval);
    this.autoplayInterval = null;
  }
}

if (!customElements.get('hero-carousel-component')) {
  customElements.define('hero-carousel-component', HeroCarouselComponent);
}
